/**
 * x_search - Search X (Twitter) in Real-Time via Grok
 *
 * Server-side streaming tool: follows the same two-phase marker pattern as web_search.
 * The handler returns a marker; grok_provider.py detects it and executes the search
 * via xAI's /responses endpoint with the x_search tool.
 *
 * Only available when using a Grok model (xAI-exclusive capability).
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';

export const x_search: SimpleDocCommand = {
  id: 'x_search',
  description: `Search X (Twitter) in real-time for posts, discussions, and trends.

**USE THIS WHEN:** You need current X posts, public opinion, trending topics, or what specific accounts are saying on X.

**NOTE:** Exclusive to Grok models — xAI has native access to X data.

**PARAMETERS:**
- query (required): What to search for on X
- handles (optional): Restrict to specific X handles, e.g. ["elonmusk", "sama"] (max 10). Cannot combine with excludedHandles.
- excludedHandles (optional): Exclude specific X handles (max 10). Cannot combine with handles.
- fromDate (optional): Start of date range, YYYY-MM-DD
- toDate (optional): End of date range, YYYY-MM-DD

**EXAMPLES:**

Search for a topic:
{ "query": "What are people saying about AI agents on X?" }

Search within date range:
{ "query": "xAI product announcements", "fromDate": "2025-01-01", "toDate": "2025-03-01" }

Search specific accounts:
{ "query": "latest thoughts on LLMs", "handles": ["sama", "karpathy"] }
`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'What to search for on X',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'query cannot be empty';
          if (value.length > 500) return 'query too long (max 500 characters)';
          return null;
        }
      },
      {
        name: 'handles',
        type: 'array',
        description: 'Restrict results to these X handles, max 10. Cannot combine with excludedHandles.',
        required: false,
        validator: (value: string[]) => {
          if (!value) return null;
          if (value.length > 10) return 'handles: maximum 10 handles allowed';
          return null;
        }
      },
      {
        name: 'excludedHandles',
        type: 'array',
        description: 'Exclude posts from these X handles, max 10. Cannot combine with handles.',
        required: false,
        validator: (value: string[]) => {
          if (!value) return null;
          if (value.length > 10) return 'excludedHandles: maximum 10 handles allowed';
          return null;
        }
      },
      {
        name: 'fromDate',
        type: 'string',
        description: 'Start date for search range, YYYY-MM-DD',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'fromDate must be YYYY-MM-DD';
          return null;
        }
      },
      {
        name: 'toDate',
        type: 'string',
        description: 'End date for search range, YYYY-MM-DD',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'toDate must be YYYY-MM-DD';
          return null;
        }
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, handles, excludedHandles, fromDate, toDate } = params;

    if (!query || query.trim().length === 0) {
      return createErrorResult('Invalid query', 'query is required and cannot be empty', 'validation');
    }

    if (handles?.length > 0 && excludedHandles?.length > 0) {
      return createErrorResult('Invalid parameters', 'handles and excludedHandles cannot both be set', 'validation');
    }

    return createSuccessResult(
      'X search will be executed by Grok',
      {
        success: true,
        query,
        metadata: {
          requiresXSearchEndpoint: true,  // detected by grok_provider.py
          provider: 'grok',
          xSearchConfig: {
            handles: handles || null,
            excludedHandles: excludedHandles || null,
            fromDate: fromDate || null,
            toDate: toDate || null,
          }
        }
      }
    );
  }
};
